<template>
    <div class="uploadcare-container">
      <slot />
    </div>
  </template>

<script>
import { accountService, uploadcareService } from '@/_services'
import uploadcare from 'uploadcare-widget'
export default {
  name: 'UploadCareV2',
  props: {
    id: { type: String, required: true }, // Unique ID for each uploader
    multiple: {
      type: Boolean,
      default: false
    },
    multipleMax: {
      type: Number
    },
    multipleMin: {
      type: Number
    },
    imagesOnly: {
      type: Boolean,
      default: false
    },
    // Default value does not match the UploadCare API default.
    previewStep: {
      type: Boolean,
      default: true
    },
    crop: {
      type: String,
      default: ''
    },
    imageShrink: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    tabs: {
      type: String,
      default: 'gallery file url camera dropbox gdrive box skydrive'
    },
    inputAcceptTypes: {
      type: String,
      default: ''
    },
    preferredTypes: {
      type: String,
      default: ''
    },
    // Default value does not match the UploadCare API default.
    systemDialog: {
      type: Boolean,
      default: true
    },
    multipartMinSize: {
      type: Number,
      default: 26214400
    },
    previewProxy: {
      type: String,
      default: ''
    },
    previewUrlCallback: {
      type: Function
    },
    cdnBase: {
      type: String,
      default: ''
    },
    doNotStore: {
      type: Boolean,
      default: false
    },
    validators: {
      type: Array
    },
    context: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      gallery: [],
      dialogRef: [],
      dialogSettings: {},
      dialogContainer: null,
      currentContext: null
    }
  },
  beforeDestroy () {
    if (this.gallery.length > 0) {
      this.gallery.forEach(image => {
        if (typeof window !== 'undefined') {
          window.removeEventListener(`Binds.currentImage-localstorage-changed-${image.uuid}`, this.selectImage)
        }
      })
    }
    this.$root.$off('open-uploadcare', this.handleGlobalUpload)
  },
  mounted () {
    // ✅ Listen for global event to trigger uploader
    this.$root.$on('open-uploadcare', this.handleGlobalUpload)

    // Add direct cancel button handling
    if (typeof window !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            const cancelButton = document.querySelector('.uploadcare--button_primary_outline')
            if (cancelButton) {
              cancelButton.addEventListener('click', () => {
                this.$root.$emit('uploadcare-cancel')
              })
            }
          }
        })
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true
      })
    }
  },
  methods: {
    handleGlobalUpload (uploadId) {
      if (uploadId === this.id) {
        this.onClick()
      }
    },
    async reqs (context) {
      const nReq = await accountService.getImages(context)
      return nReq
    },
    selectImage (uuid) {
      this.dialogRef.addFiles([
        uploadcare.fileFrom('uploaded', uuid, this.dialogSettings)
      ])
    },
    async handleContextChange (context) {
      try {
        // Store the current context
        this.currentContext = context

        // Show spinner at the start
        const spinner = document.getElementById('spinner')
        if (spinner) spinner.style.display = 'inline-block'

        // Update active tab
        const allContexts = [
          { name: 'profile', label: 'Profile' },
          { name: 'account', label: 'Conta' },
          { name: 'messages', label: 'Mensagens' },
          { name: 'questions', label: 'Questões' },
          { name: 'email', label: 'Email' },
          { name: 'all', label: 'Todos' },
          { name: 'whatsapp', label: 'Whatsapp' }
        ]

        allContexts.forEach(k => {
          const tab = document.getElementById('tab-' + k.name)
          if (tab) tab.classList.remove('active-context-upload')
        })

        const currentTab = document.getElementById('tab-' + context)
        if (currentTab) currentTab.classList.add('active-context-upload')

        // Wait for images to load
        const newItems = await this.reqs(context)

        // Display the images
        if (this.dialogContainer) {
          this.displayImages(newItems)
        }
      } catch (error) {
        console.error('Error loading images:', error)
      } finally {
        // Always hide spinner when done
        const spinner = document.getElementById('spinner')
        if (spinner) spinner.style.display = 'none'
      }
    },
    displayImages (images) {
      if (!this.dialogContainer) return

      const ul = document.createElement('ul')
      ul.setAttribute('class', 'uploadcare-items')
      ul.setAttribute('id', 'context-items')

      if (images.length > 0) {
        images.forEach(image => {
          const li = document.createElement('li')
          li.setAttribute('class', 'uploadcare-items__item')

          // Use direct method call instead of event
          li.onclick = () => this.selectImage(image.uuid)

          const img = document.createElement('img')
          img.setAttribute('crossorigin', 'anonymous')
          img.setAttribute('src', `https://ucarecdn.com/${image.uuid}/-/setfill/ffffff/-/format/jpeg/-/scale_crop/160x160/center/`)
          img.onerror = () => false

          li.appendChild(img)
          ul.appendChild(li)
        })
      }

      // Create context tabs
      const contextTabs = document.createElement('div')
      contextTabs.setAttribute('class', 'context-tabs')

      const singleTab = document.createElement('div')
      singleTab.setAttribute('class', 'each-context-tabs')

      // Create navigation with direct method calls
      const nav = document.createElement('nav')
      const contexts = [
        { id: 'all', label: 'Todas' },
        { id: 'profile', label: 'Perfil' },
        { id: 'account', label: 'Conta' },
        { id: 'messages', label: 'Mensagens' },
        { id: 'questions', label: 'Questões' },
        { id: 'email', label: 'Email' },
        { id: 'whatsapp', label: 'Whatsapp' }
      ]

      contexts.forEach(ctx => {
        const li = document.createElement('li')
        const button = document.createElement('button')
        button.id = 'tab-' + ctx.id
        button.textContent = ctx.label
        // Add active class if this is the current context
        if (ctx.id === this.currentContext) {
          button.classList.add('active-context-upload')
        }
        button.onclick = () => this.handleContextChange(ctx.id)
        li.appendChild(button)
        nav.appendChild(li)
      })

      const noImages = document.createElement('div')
      noImages.id = 'noImage'
      noImages.className = 'no-images'
      const allButton = document.createElement('button')
      allButton.onclick = () => this.handleContextChange('all')
      allButton.textContent = 'Todas'
      noImages.innerHTML = '<span>Você ainda não possui imagens nessa seção, tente ver </span>'
      noImages.querySelector('span').appendChild(allButton)

      singleTab.appendChild(nav)
      singleTab.appendChild(noImages)
      contextTabs.appendChild(singleTab)

      // Clear existing content and append new elements
      this.dialogContainer.empty()
      this.dialogContainer.append(contextTabs)
      this.dialogContainer.append(ul)

      // Show/hide no images message
      const emptyImagesMessage = document.getElementById('noImage')
      if (emptyImagesMessage) {
        emptyImagesMessage.style.display = images.length > 0 ? 'none' : 'block'
      }
    },
    async onClick () {
      const [gallery, { publicKey, signature: secureSignature, expire: secureExpire }] = await Promise.all([
        accountService.getImages(this.context),
        uploadcareService.generateHash()
      ])
      this.gallery = gallery
      // Set initial context
      this.currentContext = this.context

      const options = {
        publicKey,
        secureExpire,
        secureSignature,
        multiple: this.multiple,
        multipleMax: this.multipleMax,
        multipleMin: this.multipleMin,
        imagesOnly: this.imagesOnly,
        previewStep: this.previewStep,
        crop: this.crop,
        imageShrink: this.imageShrink,
        clearable: this.clearable,
        tabs: this.tabs,
        inputAcceptTypes: this.inputAcceptTypes,
        preferredTypes: this.preferredTypes,
        systemDialog: this.systemDialog,
        multipartMinSize: this.multipartMinSize,
        previewProxy: this.previewProxy,
        previewUrlCallback: this.previewUrlCallback,
        cdnBase: this.cdnBase,
        doNotStore: this.doNotStore,
        context: this.context
      }

      if (this.validators?.length) {
        Object.assign(options, { validators: this.validators })
      }

      // Register gallery tab without checking uploadcare.tabs
      uploadcare.registerTab('gallery', (container, button, dialogApi, settings) => {
        this.dialogRef = dialogApi
        this.dialogSettings = settings
        this.dialogContainer = container

        // Initialize spinner
        const containerTabs = document.getElementsByClassName('uploadcare--menu__items')[0]
        if (containerTabs) containerTabs.innerHTML = '<div class="lds-dual-ring" id="spinner"></div>'

        // Display initial images
        this.displayImages(this.gallery)
      })

      // Open dialog and handle file upload
      this.fileGroup = uploadcare.openDialog([], options)

      this.fileGroup.done((filePromise) => {
        if (this.multiple) {
          const promise = filePromise.promise()
          promise.done(() => {
            const files = filePromise.files()
            let completedFiles = 0
            const totalFiles = files.length

            files.forEach((fileProm) => {
              fileProm.done(async (file) => {
                if (file && file.sourceInfo && file.sourceInfo.source !== 'url') {
                  await accountService.saveImageOnAccountGallery({
                    url: file.cdnUrl,
                    name: file.name,
                    uuid: file.uuid,
                    uploadcareFileInfo: file
                  }, this.context)
                }
                completedFiles++
                this.$emit('success', file)

                if (completedFiles === totalFiles) {
                  this.$root.$emit('uploadcare-ready')
                }
              })
              fileProm.fail((err) => {
                this.$emit('error', err)
                completedFiles++

                if (completedFiles === totalFiles) {
                  this.$root.$emit('uploadcare-ready')
                }
              })
            })
          })
          promise.fail((err) => {
            this.$emit('error', err)
            this.$root.$emit('uploadcare-cancel')
          })
        } else {
          filePromise.done(async (file) => {
            if (file && file.sourceInfo && file.sourceInfo.source !== 'url') {
              await accountService.saveImageOnAccountGallery({
                url: file.cdnUrl,
                name: file.name,
                uuid: file.uuid,
                uploadcareFileInfo: file
              }, this.context)
            }
            this.$emit('success', file)
            this.$root.$emit('uploadcare-ready')
          })
          filePromise.fail((err) => {
            this.$emit('error', err)
            this.$root.$emit('uploadcare-cancel')
          })
        }
      })

      this.fileGroup.fail((err) => {
        this.$emit('error', err)
        this.$root.$emit('uploadcare-cancel')
      })

      if (this.fileGroup.dialog) {
        this.fileGroup.dialog.always(() => {
          this.dialogContainer = null
          this.currentContext = null
          this.$root.$emit('uploadcare-ready')
        })
      }
    }
  }
}
</script>
  <style lang="scss">
  .uploadcare-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: block;
  }

  .uploadcare--tab_name_gallery {
    position: relative;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    height: 100%;
    min-height: inherit;
    overflow: auto;
    display: block;
    box-sizing: border-box;
  }

  .uploadcare--tab_name_gallery .uploadcare__no-files {
    padding: 50px 0;
  }

  .uploadcare--tab_name_gallery .uploadcare-items {
    position: relative;
    margin: 0;
    padding: 10px;
    list-style-type: none;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .uploadcare--tab_name_gallery .uploadcare-items__item {
    position: relative;
    padding: 0;
    float: none;
    border: 1px solid #f2f2f2;
    box-shadow: 1px 1px 10px rgba(0,0,0,.03);
    margin: 0;
    cursor: pointer;
    width: 160px;
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &:hover {
      border: 1px solid #c9c9c9;
    }
  }

  .uploadcare--dialog {
    .context-tabs {
      background: #fff;
      border-bottom: 1px solid #eee;
      padding: 8px 16px;
      position: sticky;
      top: 0;
      z-index: 10;
      width: 100%;
      height: 60px;
      align-content: center;
      box-sizing: border-box;

      .each-context-tabs {
        nav {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 4px;
          margin: 0;

          &::-webkit-scrollbar {
            display: none;
          }

          li {
            margin: 0;
            flex-shrink: 0;

            button {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 8px 16px;
              border-radius: 24px;
              border: 1px solid #e0e0e0;
              background: #fff;
              color: #666;
              font-size: 13px;
              font-weight: 500;
              transition: all 0.2s ease;
              white-space: nowrap;
              height: 36px;

              .material-symbols-outlined {
                font-size: 18px;
                opacity: 0.8;
              }

              &:hover {
                border-color: #999;
                background: #f8f8f8;
                color: #333;

                .material-symbols-outlined {
                  opacity: 1;
                }
              }

              &.active-context-upload {
                background: #333;
                border-color: #333;
                color: #fff;

                .material-symbols-outlined {
                  opacity: 1;
                }
              }
            }
          }

          &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 40px;
            height: 100%;
            background: linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1));
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.2s ease;
          }

          &.has-scroll::after {
            opacity: 1;
          }
        }
      }
    }

    &__container {
      @media (min-width: 1210px) {
        max-width: 1090px;
      }
    }
  }

  .uploadcare--button {
    @media (min-width: 760px) {
      font-size: 17.5px;
    }

    &_icon {
      padding: 14px;
      width: 60px;
    }

    // Add these styles to remove focus outline
    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }

    &_primary_outline {
      &:focus,
      &:focus-visible {
        outline: none;
        box-shadow: none;
        border-color: #e0e0e0;
      }
    }
  }

    .lds-dual-ring {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.9);
      z-index: 9999;
      align-items: center;
      justify-content: center;

      &[style*="display: inline-block"] {
        display: flex !important;
      }

      &:after {
        content: "";
        width: 40px;
        height: 40px;
        border: 4px solid #1a8cd8;
        border-color: #1a8cd8 transparent #1a8cd8 transparent;
        border-radius: 50%;
        animation: lds-dual-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      }
    }

    @keyframes lds-dual-ring {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

  .no-images {
    button {
      color: #1a8cd8;
      font-size: 15px;
    }
  }
  </style>
